import {
  EyeOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Button, Space, Table, TableColumnsType, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useRef } from 'react'

import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { useTableColumnScroll } from '../../../../../hooks/useTableColumnScroll'
import { numberFormat } from '../../../../../utils'
import SettlementBreakdownTable from './SettlementBreakdownTable'
import { IDailyDisplayRow } from '../types'

interface Props {
  data: IDailyDisplayRow[]
  isLoading?: boolean
  onSelectDate?: (date: string) => void
  onSelectCostDate?: (date: string) => void
  onSelectPayment?: (date: string, paymentType: 'cod' | 'transfer') => void
}

// สีตามเครื่องหมาย: บวก=เขียว ลบ=แดง ศูนย์=ปกติ
const signStyle = (value: number) =>
  value > 0
    ? { color: '#389e0d' }
    : value < 0
      ? { color: '#cf1322' }
      : undefined

// เซลล์ยอดเงิน — จัดรูปแบบ + ใส่สีตามเครื่องหมาย
const moneyCell = (value: number) => (
  <span style={signStyle(value)}>{numberFormat(value)}</span>
)

// เซลล์ยอดเงิน + icon ดูรายการ (โชว์ icon เมื่อ ≠ 0 และมี handler)
const moneyCellWithView = (
  value: number,
  onView: (() => void) | undefined,
  tooltip: string,
) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 4,
    }}
  >
    {moneyCell(value)}
    {onView && value !== 0 && (
      <Tooltip title={tooltip}>
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined style={{ color: '#8c8c8c' }} />}
          onClick={onView}
        />
      </Tooltip>
    )}
  </span>
)

// หัวคอลัมน์ที่คำนวณจากคอลัมน์อื่น — ชี้ ⓘ แล้วบอกว่าเอาช่องไหนมาบวกลบกัน
const FormulaTitle = ({ title, lines }: { title: string; lines: string[] }) => (
  <Space size={4}>
    {title}
    <Tooltip
      title={<div style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</div>}
    >
      <InfoCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
    </Tooltip>
  </Space>
)

const DailyTable = ({
  data,
  isLoading,
  onSelectDate,
  onSelectCostDate,
  onSelectPayment,
}: Props) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const { canPrev, canNext, scrollPrev, scrollNext } = useTableColumnScroll(
    tableWrapperRef,
    [data.length, isLoading],
  )

  const columns: TableColumnsType<IDailyDisplayRow> = [
    {
      title: 'วันที่',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format(ONLY_DATE_FORMAT),
    },
    {
      title: 'ออเดอร์',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      render: (value: number) => numberFormat(value, 0),
    },
    {
      title: 'ชิ้น',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (value: number) => numberFormat(value, 0),
    },
    {
      title: 'COD (฿)',
      dataIndex: 'codTotal',
      key: 'codTotal',
      align: 'right',
      render: (value: number, record) =>
        moneyCellWithView(
          value,
          onSelectPayment && (() => onSelectPayment(record.date, 'cod')),
          'ดูออเดอร์ COD',
        ),
    },
    {
      title: 'โอน (฿)',
      dataIndex: 'transferTotal',
      key: 'transferTotal',
      align: 'right',
      render: (value: number, record) =>
        moneyCellWithView(
          value,
          onSelectPayment && (() => onSelectPayment(record.date, 'transfer')),
          'ดูออเดอร์โอน',
        ),
    },
    {
      title: 'ยอดรวม (฿)',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      title: 'ยอดยกเลิก (฿)',
      dataIndex: 'cancelledTotal',
      key: 'cancelledTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      title: 'ยอดขายสำเร็จ (฿)',
      dataIndex: 'successTotal',
      key: 'successTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      title: 'ต้นทุนสินค้า (฿)',
      dataIndex: 'successCostTotal',
      key: 'successCostTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      // คิดจากยอดสำเร็จเท่านั้น — ใบยกเลิกไม่เอามาคิดทั้งยอดขายและต้นทุน
      title: 'กำไรขั้นต้น (฿)',
      key: 'grossProfit',
      align: 'right',
      render: (_, record) =>
        moneyCell((record.successTotal ?? 0) - (record.successCostTotal ?? 0)),
    },
    {
      title: 'ได้รับจริง (฿)',
      dataIndex: 'netTotal',
      key: 'netTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      title: 'ค่าธรรมเนียม (฿)',
      dataIndex: 'feeTotal',
      key: 'feeTotal',
      align: 'right',
      render: moneyCell,
    },
    {
      title: 'ค่าใช้จ่ายอื่น (฿)',
      dataIndex: 'cost',
      key: 'cost',
      align: 'right',
      render: (value: number, record) =>
        moneyCellWithView(
          value,
          onSelectCostDate && (() => onSelectCostDate(record.date)),
          'ดูรายการค่าใช้จ่าย',
        ),
    },
    {
      // ยอดขายสำเร็จ − ต้นทุน − ค่าธรรมเนียม − ค่าใช้จ่ายอื่น
      title: (
        <FormulaTitle
          title="กำไรประมาณการ (฿)"
          lines={[
            'ยอดขายสำเร็จ (฿)',
            '− ต้นทุนสินค้า (฿)',
            '− ค่าธรรมเนียม (฿)  (ช่องนี้แสดงเป็นค่าติดลบอยู่แล้ว)',
            '− ค่าใช้จ่ายอื่น (฿)',
            '',
            'ค่าธรรมเนียมมีเฉพาะออเดอร์ที่ TikTok โอนเงินแล้ว — วันที่ยังไม่โอนกำไรจะสูงกว่าจริง',
          ]}
        />
      ),
      dataIndex: 'estimatedProfit',
      key: 'estimatedProfit',
      align: 'right',
      render: moneyCell,
    },
    {
      // ได้รับจริง − ต้นทุน − ค่าใช้จ่ายอื่น (เฉพาะที่ TikTok โอนแล้ว)
      title: (
        <FormulaTitle
          title="กำไรสุทธิจริง (฿)"
          lines={[
            'ได้รับจริง (฿)  (หักค่าธรรมเนียมแล้ว)',
            '− ต้นทุนสินค้า (฿)',
            '− ค่าใช้จ่ายอื่น (฿)',
            '',
            'นับเฉพาะเงินที่ TikTok โอนแล้ว — วันที่ยังโอนไม่ครบจะต่ำหรือติดลบ แล้วขยับขึ้นเมื่อโอนครบ',
          ]}
        />
      ),
      dataIndex: 'settledProfit',
      key: 'settledProfit',
      align: 'right',
      render: moneyCell,
    },
    {
      // ปุ่มเลื่อนตารางทีละคอลัมน์ — ไม่ต้องเลื่อนหน้าลงไปหา scroll bar ท้ายตาราง
      title: (
        <Space size={0}>
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            aria-label="เลื่อนตารางไปซ้าย"
            disabled={!canPrev}
            onClick={scrollPrev}
          />
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            aria-label="เลื่อนตารางไปขวา"
            disabled={!canNext}
            onClick={scrollNext}
          />
        </Space>
      ),
      key: 'action',
      align: 'right',
      // ตารางกว้างมาก — ตรึงไว้ขวาให้กดดูออเดอร์ได้โดยไม่ต้องเลื่อนไปสุด
      fixed: 'right',
      width: 80,
      render: (_, record) =>
        onSelectDate && record.count > 0 ? (
          <Tooltip title="ดูออเดอร์">
            <Button
              type="text"
              icon={<EyeOutlined style={{ color: '#8c8c8c' }} />}
              onClick={() => onSelectDate(record.date)}
            />
          </Tooltip>
        ) : null,
    },
  ]

  const totals = data.reduce(
    (acc, row) => ({
      count: acc.count + row.count,
      quantity: acc.quantity + row.quantity,
      codTotal: acc.codTotal + row.codTotal,
      transferTotal: acc.transferTotal + row.transferTotal,
      grandTotal: acc.grandTotal + row.grandTotal,
      cancelledTotal: acc.cancelledTotal + row.cancelledTotal,
      successTotal: acc.successTotal + row.successTotal,
      successCostTotal: acc.successCostTotal + row.successCostTotal,
      netTotal: acc.netTotal + row.netTotal,
      feeTotal: acc.feeTotal + row.feeTotal,
      cost: acc.cost + row.cost,
      estimatedProfit: acc.estimatedProfit + row.estimatedProfit,
      settledProfit: acc.settledProfit + row.settledProfit,
    }),
    {
      count: 0,
      quantity: 0,
      codTotal: 0,
      transferTotal: 0,
      grandTotal: 0,
      cancelledTotal: 0,
      successTotal: 0,
      successCostTotal: 0,
      netTotal: 0,
      feeTotal: 0,
      cost: 0,
      estimatedProfit: 0,
      settledProfit: 0,
    },
  )

  return (
    <div ref={tableWrapperRef}>
      <Table
        rowKey="date"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          // กางแต่ละวัน → breakdown settlement ของวันนั้น (ปุ่ม +/- โดย antd)
          expandedRowRender: (row) => (
            <SettlementBreakdownTable summary={row} />
          ),
          rowExpandable: (row) => row.settledCount > 0,
        }}
        summary={() =>
          data.length ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>รวม</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  {numberFormat(totals.count, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  {numberFormat(totals.quantity, 0)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  {moneyCell(totals.codTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {moneyCell(totals.transferTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  {moneyCell(totals.grandTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  {moneyCell(totals.cancelledTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  {moneyCell(totals.successTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  {moneyCell(totals.successCostTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right">
                  {moneyCell(totals.successTotal - totals.successCostTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  {moneyCell(totals.netTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12} align="right">
                  {moneyCell(totals.feeTotal)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={13} align="right">
                  {moneyCell(totals.cost)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={14} align="right">
                  {moneyCell(totals.estimatedProfit)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={15} align="right">
                  {moneyCell(totals.settledProfit)}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={16} />
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </div>
  )
}

export default DailyTable
